import React, { useState } from 'react';
import { CheckCircle2, XCircle, Sparkles, Brain, Globe, Shield, Users, AlertTriangle, ChevronDown, ChevronRight } from 'lucide-react';

/* ─── Componenti interni ─── */

const ReviewGate = ({ execution, onApprove, onReject, onRegenerate, loading }) => {
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const handleReject = () => {
    if (rejectReason.trim()) {
      onReject(rejectReason);
      setShowRejectModal(false);
      setRejectReason('');
    }
  };

  if (execution.status === 'approved') {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-2 text-green-700">
        <CheckCircle2 className="w-5 h-5" />
        <span className="font-medium">Execution approvata — pipeline avanzata a C1.5</span>
      </div>
    );
  }

  if (execution.status === 'rejected') {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center gap-2 text-red-700">
          <XCircle className="w-5 h-5" />
          <span className="font-medium">Execution rigettata</span>
        </div>
        {execution.rejectionReason && (
          <p className="text-sm text-red-600 mt-2">{execution.rejectionReason}</p>
        )}
      </div>
    );
  }

  return (
    <>
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h3 className="font-medium text-gray-800 mb-3">Revisiona Output</h3>
        <div className="flex gap-3">
          <button onClick={() => onApprove()} disabled={loading}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50">
            <CheckCircle2 className="w-4 h-4" /> Approva
          </button>
          <button onClick={() => setShowRejectModal(true)} disabled={loading}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50">
            <XCircle className="w-4 h-4" /> Rigetta
          </button>
          <button onClick={onRegenerate} disabled={loading}
            className="flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50">
            Rigenera
          </button>
        </div>
      </div>
      {showRejectModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="font-semibold text-gray-800 mb-3">Motivo del Rigetto</h3>
            <textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)}
              className="w-full border border-gray-200 rounded-lg p-3 text-sm resize-none h-24 focus:outline-none focus:ring-2 focus:ring-purple-500"
              placeholder="Descrivi il motivo del rigetto..." />
            <div className="flex gap-3 mt-3">
              <button onClick={handleReject} disabled={!rejectReason.trim()}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 text-sm">
                Conferma Rigetto
              </button>
              <button onClick={() => setShowRejectModal(false)}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm">
                Annulla
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const EmptyState = () => (
  <div className="h-full flex items-center justify-center">
    <div className="text-center">
      <Sparkles className="w-12 h-12 text-gray-300 mx-auto mb-3" />
      <p className="text-gray-500">Nessuna execution disponibile</p>
    </div>
  </div>
);

const Section = ({ title, icon: Icon, children, color = 'purple' }) => {
  const [open, setOpen] = useState(true);
  const colors = {
    purple: 'bg-purple-50 border-purple-200 text-purple-700',
    blue: 'bg-blue-50 border-blue-200 text-blue-700',
    green: 'bg-green-50 border-green-200 text-green-700',
    orange: 'bg-orange-50 border-orange-200 text-orange-700',
    gray: 'bg-gray-50 border-gray-200 text-gray-700',
  };
  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      <button
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50 transition-colors"
        onClick={() => setOpen(o => !o)}
      >
        <div className="flex items-center gap-3">
          {Icon && <div className={`p-1.5 rounded-lg border ${colors[color]}`}><Icon className="w-4 h-4" /></div>}
          <span className="font-semibold text-gray-800 text-sm">{title}</span>
        </div>
        {open ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
      </button>
      {open && <div className="px-5 pb-5 border-t border-gray-100">{children}</div>}
    </div>
  );
};

const Tag = ({ children, color = 'gray' }) => {
  const colors = {
    gray: 'bg-gray-100 text-gray-700',
    purple: 'bg-purple-100 text-purple-700',
    blue: 'bg-blue-100 text-blue-700',
    green: 'bg-green-100 text-green-700',
    red: 'bg-red-100 text-red-700',
    orange: 'bg-orange-100 text-orange-700',
  };
  return (
    <span className={`inline-block text-xs px-2 py-0.5 rounded-full font-medium ${colors[color]}`}>
      {children}
    </span>
  );
};

const ItemList = ({ items, renderItem }) => {
  if (!items || items.length === 0)
    return <p className="text-sm text-gray-400 italic mt-3">Nessun elemento</p>;
  return (
    <div className="mt-3 space-y-3">
      {items.map((item, i) => renderItem(item, i))}
    </div>
  );
};

/* ─── Main View ─── */

const Contract0XView = ({ execution, onApprove, onReject, onRegenerate, loading }) => {
  if (!execution) return <EmptyState />;

  const output = execution.output || execution.outputData || {};

  const researchUniverse    = output.research_universe || {};
  const compatibility       = output.product_population_compatibility || {};
  const priorMap            = output.prior_map || [];
  const observationClusters = output.observation_clusters || [];
  const dominantObs         = output.dominant_observations || [];
  const observationLibrary  = output.consumer_observation_library || [];
  const higherOrder         = output.higher_order_inference_model || {};
  const marketLang          = output.market_language_model || {};
  const regionalModel       = output.regional_identity_model || {};
  const creatorModel        = output.creator_model || {};
  const winningContent      = output.winning_content_library || [];
  const marketResearch      = output.market_research || {};
  const retrieval           = output.retrieval_metadata || {};

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-violet-600 px-6 py-4 text-white flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-white/20 rounded-lg">
            <Sparkles className="w-5 h-5" />
          </div>
          <div>
            <h2 className="font-bold text-lg">C0X — Cognitive Prior Transfer Research Engine</h2>
            <p className="text-purple-200 text-sm">Percorso alternativo · Ricostruzione prior cognitivi da domini analoghi</p>
          </div>
          <div className="ml-auto">
            <span className="text-xs bg-white/20 px-2.5 py-1 rounded-full font-medium">
              {retrieval.entry_path === 'alternative' ? '✓ Percorso alternativo' : 'C0X'}
            </span>
          </div>
        </div>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto bg-gray-50 p-6 space-y-4">

        {/* Review gate */}
        {execution.status !== 'pending' && execution.status !== 'executing' && (
          <ReviewGate
            execution={execution}
            onApprove={onApprove}
            onReject={onReject}
            onRegenerate={onRegenerate}
            loading={loading}
          />
        )}

        {/* Retrieval Metadata */}
        {retrieval.direct_market_evidence_status && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-amber-800 mb-1">Stato evidenza diretta di mercato</p>
              <p className="text-sm text-amber-700">{retrieval.direct_market_evidence_status}</p>
              {retrieval.limitations?.length > 0 && (
                <ul className="mt-2 space-y-1">
                  {retrieval.limitations.map((l, i) => (
                    <li key={i} className="text-xs text-amber-600">• {l}</li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}

        {/* Research Universe (Observation Domains) */}
        <Section title="Research Universe — Observation Domains" icon={Globe} color="purple">
          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
            {['validated_observation_domains', 'selected_observation_domains', 'candidate_observation_domains', 'excluded_observation_domains'].map(key => {
              const items = researchUniverse[key] || [];
              if (!items.length) return null;
              const labels = {
                validated_observation_domains: { label: 'Validati', color: 'green' },
                selected_observation_domains: { label: 'Selezionati', color: 'purple' },
                candidate_observation_domains: { label: 'Candidati', color: 'blue' },
                excluded_observation_domains: { label: 'Esclusi', color: 'red' },
              };
              const { label, color } = labels[key];
              return (
                <div key={key}>
                  <Tag color={color}>{label} ({items.length})</Tag>
                  <ul className="mt-2 space-y-1">
                    {items.map((d, i) => (
                      <li key={i} className="text-sm text-gray-700">
                        • {typeof d === 'string' ? d : (d.domain || d.name || JSON.stringify(d))}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </Section>

        {/* Product-Population Compatibility */}
        {(compatibility.overall_compatibility_status || compatibility.compatibility_dimensions?.length > 0) && (
          <Section title="Compatibilità Prodotto-Popolazione" icon={Shield} color="orange">
            <div className="mt-3 space-y-3">
              <div className="flex gap-3">
                {compatibility.overall_compatibility_status && (
                  <Tag color={
                    compatibility.overall_compatibility_status === 'compatible' ? 'green' :
                    compatibility.overall_compatibility_status === 'incompatible' ? 'red' : 'orange'
                  }>
                    {compatibility.overall_compatibility_status}
                  </Tag>
                )}
                {compatibility.overall_adoption_friction && (
                  <Tag color={compatibility.overall_adoption_friction === 'low' ? 'green' : 'orange'}>
                    Friction: {compatibility.overall_adoption_friction}
                  </Tag>
                )}
              </div>
              {compatibility.compatibility_summary && (
                <p className="text-sm text-gray-700 leading-relaxed">{compatibility.compatibility_summary}</p>
              )}
              <ItemList
                items={compatibility.compatibility_dimensions || []}
                renderItem={(dim, i) => (
                  <div key={i} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-sm text-gray-800">{dim.dimension}</span>
                      <Tag color={dim.compatibility_status === 'compatible' ? 'green' : dim.compatibility_status === 'incompatible' ? 'red' : 'orange'}>
                        {dim.compatibility_status}
                      </Tag>
                      {dim.severity && <Tag color={dim.severity === 'blocking' ? 'red' : 'gray'}>{dim.severity}</Tag>}
                    </div>
                    {dim.recommended_response && (
                      <p className="text-xs text-gray-600">{dim.recommended_response}</p>
                    )}
                  </div>
                )}
              />
            </div>
          </Section>
        )}

        {/* Observation Clusters */}
        {observationClusters.length > 0 && (
          <Section title={`Observation Clusters (${observationClusters.length})`} icon={Brain} color="purple">
            <ItemList
              items={observationClusters}
              renderItem={(cluster, i) => (
                <div key={i} className="border border-purple-100 rounded-lg p-3 bg-purple-50/50">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-sm text-purple-800">
                      {cluster.cluster_name || cluster.name || `Cluster ${i + 1}`}
                    </span>
                    {cluster.cluster_type && <Tag color="purple">{cluster.cluster_type}</Tag>}
                  </div>
                  {cluster.latent_mechanism && (
                    <p className="text-xs text-purple-700 leading-relaxed">{cluster.latent_mechanism}</p>
                  )}
                  {cluster.observations && (
                    <p className="text-xs text-gray-500 mt-1">{cluster.observations.length} osservazioni</p>
                  )}
                </div>
              )}
            />
          </Section>
        )}

        {/* Prior Map */}
        {priorMap.length > 0 && (
          <Section title={`Prior Map — Transferred Priors (${priorMap.length})`} icon={Brain} color="blue">
            <ItemList
              items={priorMap}
              renderItem={(prior, i) => (
                <div key={i} className="border border-blue-100 rounded-lg p-3 bg-blue-50/40">
                  <div className="flex items-start gap-2 mb-2">
                    <span className="font-mono text-xs text-blue-500 shrink-0 mt-0.5">
                      {prior.prior_id || `P${i + 1}`}
                    </span>
                    <div className="flex-1 min-w-0">
                      {(prior.confidence) && (
                        <Tag color={prior.confidence === 'high' ? 'green' : prior.confidence === 'medium' ? 'orange' : 'gray'}>
                          {prior.confidence}
                        </Tag>
                      )}
                    </div>
                    {prior.source_domains?.length > 0 && (
                      <div className="flex gap-1 flex-wrap">
                        {prior.source_domains.map((d, j) => <Tag key={j} color="blue">{d}</Tag>)}
                      </div>
                    )}
                  </div>
                  {(prior.prior || prior.prior_content || prior.prior_name) && (
                    <p className="text-xs text-gray-700 leading-relaxed mb-2">
                      {prior.prior || prior.prior_content || prior.prior_name}
                    </p>
                  )}
                  {(prior.transfer_to_innovation || prior.transferability_reason) && (
                    <p className="text-xs text-blue-600 italic leading-relaxed">
                      → {prior.transfer_to_innovation || prior.transferability_reason}
                    </p>
                  )}
                  {prior.consumer_model && (
                    <p className="text-xs text-gray-400 mt-1">Modello: {prior.consumer_model}</p>
                  )}
                </div>
              )}
            />
          </Section>
        )}

        {/* Higher-Order Inference Model */}
        {higherOrder.persuasion_awareness_baseline && (
          <Section title="Higher-Order Inference Model" icon={Brain} color="gray">
            <div className="mt-3 space-y-3">
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                  Persuasion Awareness Baseline
                </p>
                <p className="text-sm text-gray-700">{higherOrder.persuasion_awareness_baseline}</p>
              </div>
              {higherOrder.manipulation_detection_triggers?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                    Manipulation Detection Triggers
                  </p>
                  <ul className="space-y-1">
                    {higherOrder.manipulation_detection_triggers.map((t, i) => (
                      <li key={i} className="text-sm text-gray-700 flex gap-2">
                        <span className="text-red-400 flex-shrink-0">⚠</span>
                        {typeof t === 'string' ? t : (t.trigger || JSON.stringify(t))}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </Section>
        )}

        {/* Market Language Model summary */}
        {(marketLang.creator_style || marketLang.speech_style) && (
          <Section title="Market Language Model" icon={Users} color="blue">
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
              {marketLang.creator_style && (
                <div>
                  <p className="text-xs font-semibold text-gray-500 mb-1">Creator Style</p>
                  <p className="text-sm text-gray-700">
                    {typeof marketLang.creator_style === 'string'
                      ? marketLang.creator_style
                      : JSON.stringify(marketLang.creator_style)}
                  </p>
                </div>
              )}
              {marketLang.speech_style && (
                <div>
                  <p className="text-xs font-semibold text-gray-500 mb-1">Speech Style</p>
                  <p className="text-sm text-gray-700">
                    {typeof marketLang.speech_style === 'string'
                      ? marketLang.speech_style
                      : JSON.stringify(marketLang.speech_style)}
                  </p>
                </div>
              )}
              {marketLang.forbidden_patterns?.length > 0 && (
                <div className="col-span-full">
                  <p className="text-xs font-semibold text-red-500 mb-1">Pattern vietati</p>
                  <div className="flex flex-wrap gap-1.5">
                    {marketLang.forbidden_patterns.map((p, i) => (
                      <Tag key={i} color="red">
                        {typeof p === 'string' ? p : (p.pattern || JSON.stringify(p))}
                      </Tag>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </Section>
        )}

        {/* Winning Content Library */}
        {winningContent.length > 0 && (
          <Section title={`Winning Content Library (${winningContent.length})`} icon={Users} color="green">
            <ItemList
              items={winningContent.slice(0, 8)}
              renderItem={(item, i) => (
                <div key={i} className="border border-green-100 rounded-lg p-3 bg-green-50/40">
                  <div className="flex items-start gap-2 mb-1">
                    <span className="font-mono text-xs text-green-500 shrink-0 mt-0.5">
                      {item.content_id || `W${i + 1}`}
                    </span>
                    <p className="font-semibold text-sm text-gray-800 flex-1">
                      {item.concept || item.content_type || item.title || `Item ${i + 1}`}
                    </p>
                  </div>
                  {(item.hook || item.why_it_works) && (
                    <p className="text-xs text-gray-600 leading-relaxed mb-2 italic">
                      "{item.hook || item.why_it_works}"
                    </p>
                  )}
                  {item.structure?.length > 0 && (
                    <ul className="text-xs text-gray-500 space-y-0.5 mb-2">
                      {item.structure.map((s, j) => (
                        <li key={j}>• {s}</li>
                      ))}
                    </ul>
                  )}
                  {item.platforms?.length > 0 && (
                    <div className="flex gap-1 flex-wrap">
                      {item.platforms.map((p, j) => <Tag key={j} color="green">{p}</Tag>)}
                    </div>
                  )}
                  {item.priors_addressed?.length > 0 && (
                    <p className="text-xs text-gray-400 mt-1">
                      Priors: {item.priors_addressed.join(', ')}
                    </p>
                  )}
                </div>
              )}
            />
          </Section>
        )}

        {/* Raw JSON fallback */}
        {Object.keys(output).length > 0 && priorMap.length === 0 && observationClusters.length === 0 && (
          <Section title="Output JSON grezzo" icon={Brain} color="gray">
            <pre className="mt-3 text-xs text-gray-600 bg-gray-50 rounded-lg p-4 overflow-x-auto max-h-96">
              {JSON.stringify(output, null, 2)}
            </pre>
          </Section>
        )}
      </div>
    </div>
  );
};

export default Contract0XView;
